import { makeAutoObservable } from "mobx";

export interface DoctorClinicMismatchContext {
  /** Display name of the doctor on the Rx (may be empty). */
  doctorName: string;
  /** Official LANR of the doctor (may be empty). */
  doctorLANR: string;
  /** Display name of the practice on the Rx (may be empty). */
  clinicName: string;
  /** Official BSNR of the practice (may be empty). */
  clinicBSNR: string;
}

/**
 * Global, promise-based confirmation shown when an Rx is about to be SAVED with
 * a LANR and a BSNR that do not belong together.
 *
 * Distinct from the join prompt next door: that one offers to CREATE the
 * doctor↔practice association and fires while a field is being edited. This one
 * changes no master data - it only asks whether to save a prescription whose
 * doctor is not registered at the practice it names. The two are separate
 * because a mismatch is not always an error (a doctor may genuinely work at a
 * practice nobody has assigned yet), and because reception must stay able to
 * record the prescription exactly as the doctor wrote it.
 *
 * `requestConfirm` resolves to `true` (save anyway) or `false` (go back).
 * Singleton, mirroring the join/notification/lunch-break prompt pattern.
 */
class DoctorClinicMismatchPromptStore {
  open = false;
  context: DoctorClinicMismatchContext | null = null;
  resolver: ((confirmed: boolean) => void) | null = null;

  constructor() {
    makeAutoObservable(this, { resolver: false });
  }

  requestConfirm(context: DoctorClinicMismatchContext): Promise<boolean> {
    this.context = context;
    this.open = true;
    return new Promise<boolean>((resolve) => {
      this.resolver = resolve;
    });
  }

  private settle(confirmed: boolean) {
    const resolve = this.resolver;
    this.resolver = null;
    this.context = null;
    this.open = false;
    resolve?.(confirmed);
  }

  confirm() {
    this.settle(true);
  }

  cancel() {
    this.settle(false);
  }
}

const doctorClinicMismatchPromptStore = new DoctorClinicMismatchPromptStore();
export default doctorClinicMismatchPromptStore;
